window.HillmalayaPickup = window.HillmalayaPickup || {};

const API = '/api/2026-10/graphql.json';

// 18 districts -> Shopify zone. Mirrors DISTRICTS in tools/pickup-points/build.py.
const ZONE = {
  '中西區': 'HK', '灣仔區': 'HK', '東區': 'HK', '南區': 'HK',
  '油尖旺區': 'KL', '深水埗區': 'KL', '九龍城區': 'KL', '黃大仙區': 'KL', '觀塘區': 'KL',
  '荃灣區': 'NT', '屯門區': 'NT', '元朗區': 'NT', '北區': 'NT', '大埔區': 'NT',
  '沙田區': 'NT', '西貢區': 'NT', '葵青區': 'NT', '離島區': 'NT'
};

// The 19th bucket: rows whose address resolved no district (21 of 3,610 — a carrier that
// wrote no district word and no vicinity our table knows). They were previously reachable
// only by text search and were missing from the seed entirely, so the finder's district
// counts did not sum to the point total. A SENTINEL, never a real key: no district_zh can
// equal it, so `filter()` can tell "show the unresolved ones" from "show everything".
// LABELS ARE MIRRORED in tools/pickup-points/build.py (OTHER_KEY/OTHER_ZH/OTHER_EN) — the
// seed hard-codes them into markup for crawlers, this file uses them on the cart, where no
// seed is rendered. Change one, change both.
const OTHER = '__other';
const OTHER_ZH = '其他地區';
const OTHER_EN = 'Other areas';

// Single choke point for every mutation/query — throws (never resolves-as-success) on
// an HTTP failure, a non-JSON body (e.g. the 429-redirect-to-homepage the brief warns
// about), or a top-level GraphQL `errors` array. Callers must not be able to read a
// thrown/rejected read as "zero results".
function gql(query, variables) {
  return fetch(window.Shopify.routes.root.replace(/\/$/, '') + API, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': window.PXUTheme.storefrontToken
    },
    body: JSON.stringify({ query: query, variables: variables || {} })
  }).then(r => {
    if (!r.ok) throw new Error('storefront api http ' + r.status);
    return r.json();
  }).then(r => {
    if (r.errors && r.errors.length) throw new Error(r.errors.map(e => e.message).join('; '));
    if (!r.data) throw new Error('storefront api returned no data');
    return r;
  });
}

HillmalayaPickup._cartToken = null;

HillmalayaPickup.cartGid = function () {
  const t = HillmalayaPickup._cartToken;
  // An EMPTY cart's token is a bare 32-hex string and is NOT a Storefront cart id.
  // Absence of ?key= means "no cart yet", never failure.
  return t && t.indexOf('?key=') > -1 ? 'gid://shopify/Cart/' + t : null;
};

HillmalayaPickup.refreshCart = function () {
  return fetch(window.Shopify.routes.root + 'cart.js')
    .then(r => r.json())
    .then(c => { HillmalayaPickup._cartToken = c.token; return c; });
};

const ADDR_FIELDS =
  'addresses { id selected oneTimeUse address { ... on CartDeliveryAddress { address1 city provinceCode countryCode } } }';

// Resolves to [] only for a genuinely empty/nonexistent cart (no gid, or a cart the
// API confirms has no addresses). A failed read REJECTS — gql() throws rather than
// handing back a falsy `data` we'd otherwise collapse into "zero addresses". Callers
// (clearAddress) must not confuse "read succeeded, zero addresses" with "read failed".
HillmalayaPickup._readAddresses = function () {
  return HillmalayaPickup.refreshCart().then(() => {
    const gid = HillmalayaPickup.cartGid();
    if (!gid) return [];
    return gql('query($id: ID!){ cart(id:$id){ delivery { ' + ADDR_FIELDS + ' } } }', { id: gid })
      .then(r => (r.data.cart && r.data.cart.delivery.addresses) || []);
  });
};

HillmalayaPickup.setAddress = function (point) {
  return HillmalayaPickup.refreshCart().then(() => {
    const gid = HillmalayaPickup.cartGid();
    if (!gid) return { ok: false, addressId: null, error: 'no cart' };
    const M = 'mutation($id: ID!, $a: [CartSelectableAddressInput!]!){' +
      ' cartDeliveryAddressesAdd(cartId:$id, addresses:$a){' +
      ' cart { delivery { ' + ADDR_FIELDS + ' } } userErrors { message } } }';
    return gql(M, {
      id: gid,
      a: [{
        address: { deliveryAddress: {
          address1: point.code + ' ' + point.name_zh,   // code FIRST, Chinese always
          city: point.district_zh || '',
          provinceCode: ZONE[point.district_zh] || null,
          countryCode: 'HK'
        }},
        selected: true,
        oneTimeUse: true
      }]
    }).then(r => {
      const d = r.data && r.data.cartDeliveryAddressesAdd;
      const errs = (d && d.userErrors) || [];
      if (!d || errs.length) return { ok: false, addressId: null, error: JSON.stringify(errs) };
      const list = d.cart.delivery.addresses;
      return { ok: true, addressId: list.length ? list[list.length - 1].id : null, error: null };
    });
  }).catch(e => ({ ok: false, addressId: null, error: e.message || String(e) }));
};

HillmalayaPickup.clearAddress = function () {
  return HillmalayaPickup._readAddresses().then(list => {
    // Delete only what the PICKER owns. setAddress writes oneTimeUse:true, so anything
    // else on the cart is somebody else's — plausibly a copy of a logged-in buyer's saved
    // default, which we cannot check without a customer session and must not gamble on.
    // A narrower delete costs nothing and removes an unverifiable risk.
    const mine = list.filter(a => a.oneTimeUse);
    if (!mine.length) return { ok: true, removed: 0, error: null };
    const M = 'mutation($id: ID!, $ids: [ID!]!){' +
      ' cartDeliveryAddressesRemove(cartId:$id, addressIds:$ids){' +
      ' cart { delivery { ' + ADDR_FIELDS + ' } } userErrors { message } } }';
    return gql(M, { id: HillmalayaPickup.cartGid(), ids: mine.map(a => a.id) }).then(r => {
      const d = r.data && r.data.cartDeliveryAddressesRemove;
      const errs = (d && d.userErrors) || [];
      if (!d || errs.length) return { ok: false, removed: 0, error: JSON.stringify(errs) };
      return { ok: true, removed: list.length - d.cart.delivery.addresses.length, error: null };
    });
  }).catch(e => ({ ok: false, removed: 0, error: e.message || String(e) }));
};

// ⚠️ THE CART SAYING A POINT IS CHOSEN AND CHECKOUT HAVING ITS ADDRESS ARE TWO DIFFERENT
// FACTS, AND THEY COME APART. A logged-in buyer who picks an address-book address at
// checkout replaces ours; the five pickup_* attributes survive untouched. hm-checkout-
// validation then blocks (rule 3: the address no longer carries the code) and tells them to
// choose a pickup point in the cart — which the cart shows they already have.
//
// Before this, "go back to the cart" was advice with nothing behind it: the chosen block is
// rendered from the attributes, repaintPickup only calls render(), and setAddress had exactly
// ONE caller — inside setPickup, reached only by clicking Choose on a row. So the buyer
// returned to a cart that agreed with them, pressed Checkout, and hit the identical block.
// The same failure family as the wishlist retry that consumed its own token: a message that
// names a fix the product does not implement.
//
// The write is skipped unless the SELECTED address is actually wrong — an address of ours
// that exists but is not selected is still wrong, because checkout uses the selected one.
HillmalayaPickup.reassertAddress = function () {
  const cart = window.PXUTheme.jsAjaxCart;
  if (!cart) return Promise.resolve({ ok: true, action: 'no-cart-js' });
  // Never race setPickup/clearPickup: they write the same address and share this lock.
  if (cart._pickupBusy) return Promise.resolve({ ok: true, action: 'busy' });
  return HillmalayaPickup.refreshCart().then(c => {
    const a = (c && c.attributes) || {};
    const code = String(a.pickup_code || '').trim();
    if (!code) return { ok: true, action: 'no-point' };
    return HillmalayaPickup._readAddresses().then(list => {
      const carries = x => String((x.address && x.address.address1) || '').indexOf(code) > -1;
      if (list.some(x => x.selected && carries(x))) {
        return { ok: true, action: 'already-correct' };
      }
      // Rebuilt from the attributes, not from the point index: these are the same three
      // fields setPickup fed setAddress, so the 1.2 MB index is not needed to repair this.
      const point = { code: code, name_zh: a.pickup_name || '',
                      district_zh: a.pickup_region || '' };
      cart._pickupBusy = true;
      // Hold the scrim across BOTH round trips, the same reason setPickup does: without it
      // the checkout button is live while the address is still the wrong one, which is the
      // very state being repaired.
      cart._pickupWriting = true;
      cart.setReconciling(true);
      const release = r => {
        cart._pickupBusy = false;
        cart._pickupWriting = false;
        cart.setReconciling(false);
        return r;
      };
      return HillmalayaPickup.clearAddress()
        .then(() => HillmalayaPickup.setAddress(point))
        // setAddress REJECTS on a network error or a 429; unhandled that would skip
        // release() and strand _pickupBusy, silently killing every later Choose click.
        .catch(e => ({ ok: false, error: (e && e.message) || String(e) }))
        .then(r => {
          if (!r.ok) console.warn('pickup address re-assert failed', r.error);
          return release({ ok: !!r.ok, action: r.ok ? 'reasserted' : 'failed' });
        });
    });
  }).catch(e => ({ ok: false, action: 'error', error: (e && e.message) || String(e) }));
};

// ponytail: cartGid() is documented synchronous, so something has to have populated
// _cartToken before a caller can trust it. Fire once at load rather than making every
// call site await a refresh first; Task 3/4 call sites still refresh before writes.
HillmalayaPickup.refreshCart();

// ⚠️ The Shopify CDN minifies assets/*.js at serve time and has been observed rewriting a
// LITERAL dynamic import(...) into a broken require(...) — see the z__jsKiosk.js entry in
// theme/CLAUDE.md. The rewrite is content-dependent, so "it worked in another file" proves
// nothing. Build the import through new Function so the minifier's static analysis never
// sees it, and KEEP the indirection even if a literal form appears to work after an edit.
const dynImport = new Function('u', 'return import(u)');

// label is the carrier <select>'s option text — brand names, so no | t needed.
const CARRIERS = [
  { key: 'SF', label: 'SF Express' },
  { key: 'JD', label: 'JD Logistics' },
  { key: 'CAINIAO', label: 'Cainiao' }
];

let _points = null, _loading = null;

HillmalayaPickup.load = function () {
  if (_points) return Promise.resolve(_points);
  if (_loading) return _loading;
  // Assets are ES modules exporting a flat array. Each url comes WHOLE from
  // window.PXUTheme.pickupAssets (js-variables.liquid) with its ?v= fingerprint intact —
  // that query is the only thing that makes a refreshed index visible to a returning
  // browser, since the CDN serves these max-age=31557600 either way. These are NOT
  // locale-prefixed (they are assets, not routes).
  _loading = Promise.all(CARRIERS.map(c => {
    const url = (window.PXUTheme.pickupAssets || {})[c.key];
    return dynImport(url)
      .then(m => m.default.map(p => Object.assign({ carrier: c.key }, p)))
      .catch(e => {
        // A failed/404'd carrier index must not read as "0 matches for that carrier" —
        // name it so it's distinguishable from a genuine empty result.
        console.warn('pickup index failed to load: ' + c.key + ' ' + url, e);
        return [];
      });
  })).then(lists => {
    _points = lists.reduce((a, b) => a.concat(b), []);
    _loading = null;
    return _points;
  });
  return _loading;
};


// --- carrying a point from the finder page to the next cart --------------------------
// The finder (templates/page.pickup-points.json) has no cart to write to — a visitor
// arriving from a search usually has no cart at all — so choosing there REMEMBERS the
// point and the cart offers it back. Deliberately an offer and not an automatic write: a
// silent cart mutation from a decision made on another page, possibly weeks earlier, is
// the class of unrequested customer-facing write this theme guards against everywhere.
const PENDING_KEY = 'hm:pickup-pending';
// Long enough to survive browse-now-buy-later, short enough that a counter closed since is
// unlikely to still be offered. Age is the cheap guard; _offerPending's index check below
// is the real one.
const PENDING_MAX_AGE = 30 * 24 * 60 * 60 * 1000;

// Every localStorage access is wrapped: it throws outright in some privacy modes and when
// a quota is full, and this is a convenience — it must never take the picker down with it.
HillmalayaPickup._readPending = function () {
  try {
    const raw = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
    if (!raw || !raw.p || !raw.at) return null;
    if (Date.now() - raw.at > PENDING_MAX_AGE) return null;
    return raw.p;
  } catch (e) {
    return null;
  }
};

// Returns whether the write actually happened, because the caller tells the customer it
// did. Reporting "Saved" over a storage exception would be a lie they then act on.
HillmalayaPickup._writePending = function (p) {
  try {
    if (p) localStorage.setItem(PENDING_KEY, JSON.stringify({ at: Date.now(), p: p }));
    else localStorage.removeItem(PENDING_KEY);
    return true;
  } catch (e) {
    return false;
  }
};

HillmalayaPickup._offerPending = function (root, all) {
  const box = root.querySelector('[data-pickup-pending]');
  if (!box || box._pickupOffered) return;
  const chosen = root.querySelector('[data-pickup-chosen]');
  // A cart that already has a point is settled; do not second-guess it with an older one.
  if (!chosen || !chosen.hidden) return;
  const saved = HillmalayaPickup._readPending();
  if (!saved) return;
  // ⚠️ A point that has left the index since must NEVER be offered. Writing its address
  // would send the buyer to checkout to be stopped by hm-checkout-validation with nothing
  // explaining why — the exact failure this whole feature exists to remove. Matching on
  // the live row also means an address corrected since is the one that gets written.
  const live = all.filter(x => x.carrier === saved.carrier && x.code === saved.code)[0];
  if (!live) {
    HillmalayaPickup._writePending(null);
    return;
  }
  box.querySelector('[data-pickup-pending-name]').textContent =
    live.name_zh || live.name_en || live.code;
  box._pickupPoint = live;
  box._pickupOffered = true;
  box.hidden = false;
};

HillmalayaPickup.filter = function (points, opts) {
  const district = (opts && opts.district) || '';
  const carrier = (opts && opts.carrier) || '';
  const text = ((opts && opts.text) || '').trim().toLowerCase();
  return points.filter(p => {
    if (district === OTHER) { if (p.district_zh) return false; }
    else if (district && p.district_zh !== district) return false;
    if (carrier && p.carrier !== carrier) return false;
    if (!text) return true;
    // name_en is EMPTY on all 3,606 rows (no carrier publishes an English shop name), so
    // this clause is dead today. Kept because addr_en is populated and the build could
    // start filling name_en without anyone remembering to re-add the search over it.
    return (p.name_zh || '').toLowerCase().indexOf(text) > -1
        || (p.name_en || '').toLowerCase().indexOf(text) > -1
        || (p.addr_zh || '').toLowerCase().indexOf(text) > -1
        || (p.addr_en || '').toLowerCase().indexOf(text) > -1
        || (p.code || '').toLowerCase().indexOf(text) > -1;
  });
};

// English hours translation, longest-token-first (so `公眾假期休息` matches whole
// before its own substrings `公眾假期`/`休息` do). Order in this list IS the match
// order — do not resort it.
const HOURS_MAP = [
  ['公眾假期照常營業', 'public holidays open'],
  ['星期日及公眾假期休息', 'Sun & public holidays closed'],
  ['公眾假期營業時間', 'public holiday hours'],
  ['勞工假期照常營業', 'Labour Day open'],
  ['勞工假期休息', 'Labour Day closed'],
  ['星期日及公眾假期', 'Sun & public holidays'],
  ['星期日/公眾假期', 'Sun/PH'],
  ['公眾假期休息', 'public holidays closed'],
  ['星期日/假期', 'Sun/PH'],
  ['星期一至五', 'Mon-Fri'],
  ['星期一至六', 'Mon-Sat'],
  ['星期一至日', 'Mon-Sun'],
  ['星期六休息', 'Sat closed'],
  ['星期日休息', 'Sun closed'],
  ['不設午休', 'no lunch break'],
  ['午休時間', 'lunch break'],
  ['公眾假期', 'public holidays'],
  ['營業時間', 'hours'],
  ['星期六', 'Sat'],
  ['星期日', 'Sun'],
  ['星期一', 'Mon'],
  ['星期二', 'Tue'],
  ['星期三', 'Wed'],
  ['星期四', 'Thu'],
  ['星期五', 'Fri'],
  ['午休', 'lunch break'],
  ['24小時', '24 hours'],
  ['休息', 'closed']
];

// A half-translated string reads worse than an untranslated one — if any CJK
// character survives the pass (a festival note like 中秋節, a date literal), return
// the ORIGINAL Chinese verbatim rather than a mixture.
HillmalayaPickup._translateHours = function (hours) {
  if (!hours) return hours;
  let out = hours;
  // ⚠️ Each replacement is padded with a trailing space, then the whitespace is collapsed.
  // The source needs no spaces because CJK does not use them — 星期一至五09:00-20:00 reads
  // fine — but a token-for-token swap inherits that and yields "Mon-Fri09:00-20:00,
  // Sun/PHclosed", which shipped to live before anyone rendered it. The unit assertions
  // could not catch it: they compared translated strings, and the spacing is only wrong
  // where a token ABUTS the untranslated remainder.
  HOURS_MAP.forEach(pair => { out = out.split(pair[0]).join(pair[1] + ' '); });
  out = out.replace(/\s+/g, ' ').replace(/\s*,\s*/g, ', ').trim();
  return /[一-鿿]/.test(out) ? hours : out;
};

const BUILDING_RE = /\b(Building|Bldg|Tower|Centre|Center|Mansion|Court|Plaza|House|Estate|Block|Industrial|Mall|Garden|Gardens|Terrace|Complex|Arcade|Square|Village)\b/i;
const DISTRICT_RE = /\bDistrict\b/i;

// name_en is empty on every row (see filter()'s comment) and no carrier publishes
// one, so an English label is composed from addr_en: the building segment, plus
// the locality immediately before the "…District" segment when that differs from
// the building itself. No building segment (or no addr_en at all, e.g. every JD
// row) -> '', and the caller falls back to name_zh. Never emit a partial name.
// The carrier's own generic wrapper, which is a FIXED TOKEN and not a name — safe to
// translate outright, unlike the shop/business name it sits beside (鑫龍運輸, 昌明士多),
// which is a proper noun and is deliberately left in Chinese. Counted across all 3,606
// rows: 順豐自助櫃 1054, 京東代收點 422, 京東服務站 411, 菜鳥智能櫃 266, 智郵站 160,
// 順豐站 140 — 2,453 rows, most of them SF lockers that _composeName can never label
// because a locker has no building in its address.
const CARRIER_TOKENS = [
  ['順豐自助櫃', 'SF Locker'],
  ['順豐站', 'SF Station'],
  ['京東服務站', 'JD Service Station'],
  ['京東代收點', 'JD Collection Point'],
  ['京東自提點', 'JD Pickup Point'],
  ['菜鳥智能櫃', 'Cainiao Locker'],
  ['智郵站', 'Smart Post Station']
];

// Last resort in the name ladder, and deliberately a PARTIAL translation: an English
// reader learns what KIND of place it is while the business name stays in its own script.
// That is normal bilingual practice for a proper noun, and unlike the half-translated
// hours it is not a broken string — the untranslated part is a name, not vocabulary.
// A point's name is a PLACE plus a FACILITY TYPE — 港佳商業大廈 + 京東服務站 — and both carry
// meaning. Substituting an English building name for the whole thing dropped the type, so
// 港佳商業大廈京東服務站 rendered as bare "Kingpower Commercial Building": a building, not a
// pickup point, and no indication of whether it is a staffed counter or a locker. The carrier
// BADGE says JD but not which kind. So translate the parts IN PLACE and keep the original word
// order — the type leads in 順豐自助櫃-上環 and trails in 港佳商業大廈京東服務站, and an in-place
// swap preserves whichever it is without needing to know.
// A Chinese place name that denotes a BUILDING, by its structure suffix. Deliberately
// excludes 市集 (market) and 領點 (collection point): 良景市集 resolved to "Leung King
// Estate Community Centre", a different premises in the same estate.
const BUILDING_ZH_RE = /(廈|樓|場|中心|邨|苑|園|城|閣|臺|台|軒|居|大堂)$/;

HillmalayaPickup._carrierLabel = function (nameZh, placeEn) {
  if (!nameZh) return '';
  let out = nameZh;
  // Swap the place first: the Chinese place is whatever remains once the carrier token is
  // removed, so it must be located BEFORE that token is replaced with English.
  if (placeEn) {
    let placeZh = nameZh;
    CARRIER_TOKENS.forEach(pair => { placeZh = placeZh.split(pair[0]).join(''); });
    placeZh = placeZh.replace(/^[\s\-－—:：]+|[\s\-－—:：]+$/g, '');
    // ⚠️ Substitute ONLY when the Chinese place is itself a BUILDING — it ends in a
    // structure word. The English place comes from the address, so it names the building;
    // swapping it in over a SHOP name replaces the thing the customer looks for on the
    // shopfront with the block it sits in: 鑫龍運輸 (a transport company) became "Maisy
    // Building", 葵芳繽紛便利 (a convenience store) became "Kwai Fong Plaza". Wing's rule is
    // that business names are not translated without high confidence, and a shared address
    // is not that. 快樂樓 -> Happy Building is right because 快樂樓 IS the building.
    // A numeric/latin remnant ("A35", "711科技大學") is a unit reference, not a place.
    if (placeZh.length >= 2 && BUILDING_ZH_RE.test(placeZh)) {
      out = out.split(placeZh).join(' ' + placeEn + ' ');
    }
  }
  // ⚠️ Pad every substitution rather than fixing boundaries afterwards. The CJK/Latin
  // rule below cannot help once the place has been swapped to English: 'Building'+'JD'
  // is Latin-next-to-Latin. Third instance of this family today (hours, carrier token,
  // and now place) — pad at the SWAP, collapse once at the end.
  CARRIER_TOKENS.forEach(pair => { out = out.split(pair[0]).join(' ' + pair[1] + ' '); });
  if (out.replace(/\s+/g, '') === nameZh.replace(/\s+/g, '')) return '';           // nothing swapped — no gain, keep the original
  // ⚠️ Space the CJK/Latin boundary. The source needs no spaces, so a token swap yields
  // "快樂樓JD Service Station" — the identical defect that shipped in _translateHours
  // earlier the same day. A swap into CJK text is never just a swap.
  return out
    .replace(/([一-鿿])([A-Za-z])/g, '$1 $2')
    .replace(/([A-Za-z])([一-鿿])/g, '$1 $2')
    .replace(/\s*[-－—]\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();
};

// Just the building segment of an English address — the place part of a point's name,
// without the locality that _composeName appends for a standalone label.
HillmalayaPickup._buildingOf = function (addrEn) {
  if (!addrEn) return '';
  const segs = addrEn.replace(/，/g, ',').split(',').map(s => s.trim()).filter(Boolean);
  return segs.find(s => BUILDING_RE.test(s)) || '';
};

HillmalayaPickup._composeName = function (addrEn) {
  if (!addrEn) return '';
  const segs = addrEn.replace(/，/g, ',').split(',').map(s => s.trim()).filter(Boolean);
  const buildingIdx = segs.findIndex(s => BUILDING_RE.test(s));
  if (buildingIdx === -1) return '';
  const building = segs[buildingIdx];
  const districtIdx = segs.findIndex(s => DISTRICT_RE.test(s));
  const locality = districtIdx > 0 ? segs[districtIdx - 1] : null;
  return (locality && locality !== building) ? (building + ', ' + locality) : building;
};

const ZONE_ORDER = { HK: 0, KL: 1, NT: 2 };

HillmalayaPickup.districts = function (points) {
  const seen = {};
  points.forEach(p => {
    if (!p.district_zh) return;   // never invent a bucket for an unresolved point
    if (!seen[p.district_zh]) seen[p.district_zh] = { zh: p.district_zh, en: p.district_en, count: 0 };
    seen[p.district_zh].count++;
  });
  const out = Object.keys(seen).map(k => seen[k]).sort((a, b) =>
    (ZONE_ORDER[ZONE[a.zh]] - ZONE_ORDER[ZONE[b.zh]]) || a.zh.localeCompare(b.zh));
  // Appended AFTER the sort, never sorted with the rest: ZONE has no entry for the
  // sentinel, so the comparator would evaluate `undefined - n` = NaN and the whole order
  // becomes implementation-defined. Last is also where it belongs for a reader.
  const other = points.filter(p => !p.district_zh).length;
  if (other) out.push({ zh: OTHER_ZH, en: OTHER_EN, key: OTHER, count: other });
  return out;
};

HillmalayaPickup.selftest = async function () {
  const P = window.HillmalayaPickup;
  const cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
  console.assert(cart.item_count > 0, 'SELFTEST NEEDS A NON-EMPTY CART — add something first');

  // cartGid only builds from a token carrying ?key=
  const gid = P.cartGid();
  console.assert(gid && gid.indexOf('?key=') > -1, 'cartGid must carry ?key=', gid);

  const point = { carrier: 'SF', code: '852M', name_zh: '上環文樂商廈順豐站',
                  addr_zh: '香港島中西區文咸東街91號', district_zh: '中西區' };
  const set = await P.setAddress(point);
  console.assert(set.ok && set.addressId, 'setAddress failed', set);

  const after = await P._readAddresses();
  console.assert(after.length === 1, 'expected exactly 1 address, got ' + after.length);
  console.assert(after[0].address.address1 === '852M 上環文樂商廈順豐站',
    'address1 must be "{code} {name}"', after[0].address.address1);
  console.assert(after[0].address.city === '中西區', 'city must be the district');
  console.assert(after[0].address.provinceCode === 'HK', '中西區 must map to zone HK');
  console.assert(after[0].selected === true && after[0].oneTimeUse === true, 'flags wrong');

  const cleared = await P.clearAddress();
  console.assert(cleared.ok && cleared.removed === 1, 'clearAddress failed', cleared);
  const empty = await P._readAddresses();
  // clearAddress only deletes what the picker owns (oneTimeUse), so assert on that rather
  // than on a total that a logged-in buyer's saved default could legitimately inflate.
  console.assert(empty.filter(x => x.oneTimeUse).length === 0,
    'pickup address survived clearAddress — ' + empty.filter(x => x.oneTimeUse).length + ' left');

  // Address-origin labels. Pure, no network — the geocode itself is exercised by using the
  // field, but the label is what a buyer reads when deciding which candidate is theirs.
  const piped = { nameEN: '8 THISTLE STREET|999 CANTON ROAD', nameZH: '荃錦公路',
    addressEN: '999 CANTON ROAD  ', addressZH: '廣東道   999號',
    districtEN: 'Yau Tsim Mong District', districtZH: '油尖旺區' };
  console.assert(P._addrLabel(piped, false).name === '8 THISTLE STREET / 999 CANTON ROAD',
    'a piped CSDI name must render as a separator, not a raw bar — got "'
      + P._addrLabel(piped, false).name + '"');
  console.assert(P._addrLabel(piped, false).sub === '999 CANTON ROAD, Yau Tsim Mong District',
    'en sub must collapse the API runs of whitespace — got "' + P._addrLabel(piped, false).sub + '"');
  console.assert(P._addrLabel(piped, true).name === '荃錦公路'
    && P._addrLabel(piped, true).sub === '廣東道 999號, 油尖旺區',
    'zh locale must read the zh fields');
  // A row with no English name at all must still be tappable rather than blank — the label
  // falls through to the address, never to ''.
  console.assert(P._addrLabel({ nameEN: '', nameZH: '', addressEN: '1 SOMEWHERE ST' }, false).name
    === '1 SOMEWHERE ST', 'a nameless row must fall back to its address');

  const all = await P.load();
  console.assert(all.length === 3606, 'expected 3606 points, got ' + all.length);
  console.assert(all.filter(p => p.carrier === 'SF').length === 1637, 'SF count wrong');
  console.assert(all.filter(p => p.carrier === 'CAINIAO').length === 957, 'Cainiao count wrong');
  console.assert(all.filter(p => p.carrier === 'JD').length === 1012, 'JD count wrong');
  console.assert(await P.load() === all, 'load() must cache, not refetch');

  // free text matches both languages and both fields
  const byEn = P.filter(all, { text: 'Kwun Tong' });
  const byZh = P.filter(all, { text: '觀塘' });
  console.assert(byEn.length > 0 && byZh.length > 0, 'text filter must work in both languages');
  const mall = P.filter(all, { text: '寶盈花園' });
  console.assert(mall.length > 0, 'must match a mall name inside addr_zh');

  // district filter, and it composes with text
  const kt = P.filter(all, { district: '觀塘區' });
  console.assert(kt.length > 0 && kt.every(p => p.district_zh === '觀塘區'), 'district filter leaks');
  console.assert(P.filter(all, { district: '觀塘區', text: 'zzzznomatch' }).length === 0,
    'district and text must AND, not OR');

  // unresolved districts land in the "Others" bucket rather than falling out of the
  // filters entirely, so the counts now sum to the WHOLE population, not the resolved one.
  const noDistrict = all.filter(p => !p.district_zh);
  console.assert(noDistrict.length > 0, 'expected some unresolved districts');
  const ds = P.districts(all);
  console.assert(ds.length === 19, 'expected 18 districts + Others, got ' + ds.length);
  console.assert(ds[ds.length - 1].key === '__other', '"Others" must sort last');
  console.assert(ds[ds.length - 1].count === noDistrict.length,
    'Others must hold exactly the unresolved rows');
  console.assert(ds.reduce((n, d) => n + d.count, 0) === all.length,
    'district counts must now sum to the FULL population');
  // The sentinel must select the unresolved rows and nothing else — and must not be read
  // as a falsy "no filter", which is what an empty-string key would have done.
  const oth = P.filter(all, { district: '__other' });
  console.assert(oth.length === noDistrict.length && oth.every(p => !p.district_zh),
    'Others filter leaks: got ' + oth.length + ' of ' + noDistrict.length);
  console.assert(P.filter(all, { district: '__other', text: 'zzzznomatch' }).length === 0,
    'Others must AND with text, not OR');

  console.log('pickup bridge selftest ok');

  // English hours: longest-first ordering and the hard no-half-translation rule.
  console.assert(P._translateHours('24小時') === '24 hours', '24小時 must translate to 24 hours');
  console.assert(P._translateHours('公眾假期休息') === 'public holidays closed',
    'longest match must win over its own substrings 公眾假期/休息, got "' +
    P._translateHours('公眾假期休息') + '"');
  const untranslatable = '中秋節休息';
  const halfTranslated = P._translateHours(untranslatable);
  console.assert(halfTranslated === untranslatable,
    'a string with an untranslatable token (中秋節) must fall back to the ORIGINAL ' +
    'verbatim, not a mixture — got "' + halfTranslated + '"');
  console.assert(
    !/[一-鿿]/.test(P._translateHours('星期一至五09:00-20:00,星期六09:00-20:00,星期日/假期休息')),
    'a fully-translatable hours string must contain no CJK afterwards');
  // ⚠️ Absence of CJK is NOT the same as readable English. The check above passed on
  // "Mon-Fri09:00-20:00,Sat09:00-20:00,Sun/PHclosed", which shipped to live: the source
  // needs no spaces because CJK does not use them, and a token swap inherits that. Assert
  // the exact string, so spacing is pinned rather than merely implied.
  console.assert(
    P._translateHours('星期一至五09:00-20:00,星期六09:00-20:00,星期日/假期休息') ===
      'Mon-Fri 09:00-20:00, Sat 09:00-20:00, Sun/PH closed',
    'translated hours must be spaced and comma-separated — got "' +
      P._translateHours('星期一至五09:00-20:00,星期六09:00-20:00,星期日/假期休息') + '"');
  // The whitespace collapse must not touch a string that falls back: a mangled Chinese
  // original would be worse than the untranslated one it is supposed to preserve.
  console.assert(P._translateHours('9月17日提早18:00休息') === '9月17日提早18:00休息',
    'an untranslatable hours string must fall back BYTE-for-byte, unspaced');

  // Carrier tokens are fixed wrappers, not names — safe to translate. The business name
  // beside them is a proper noun and stays Chinese, which is normal bilingual practice and
  // NOT the half-translated defect the hours rule guards against.
  console.assert(P._carrierLabel('順豐自助櫃-上環') === 'SF Locker - 上環',
    'SF locker token must translate, area kept — got "' + P._carrierLabel('順豐自助櫃-上環') + '"');
  console.assert(P._carrierLabel('快樂樓京東服務站') === '快樂樓 JD Service Station',
    'a CJK name abutting an English token must be SPACED — got "' +
      P._carrierLabel('快樂樓京東服務站') + '"');
  // No carrier token means nothing was gained; return empty so the ladder falls through to
  // name_zh rather than handing back an identical string dressed up as a translation.
  console.assert(P._carrierLabel('鑫龍運輸') === '',
    'a name with no carrier token must return empty, not itself');

  // English names, composed from addr_en — verified against the real committed rows.
  console.assert(
    P._composeName('Shop A, G/F, Man Lok Building, No. 91 Bonham Strand, Sheung Wan, ' +
      'Central and Western District, Hong Kong Island, Hong Kong') === 'Man Lok Building, Sheung Wan',
    '852M must compose to "Man Lok Building, Sheung Wan"');
  console.assert(
    P._composeName('Unit D, G/F, Winbase Centre, No. 208 Queen\'s Road Central, Central, ' +
      'Central and Western District,Hong Kong Island, Hong Kong') === 'Winbase Centre, Central',
    '852MB must compose to "Winbase Centre, Central"');
  console.assert(
    P._composeName('2/F, Lok Wah Shopping Centre, No. 70 Chun Wah Road, Ngau Tau Kok, Kwun Tong')
      === 'Lok Wah Shopping Centre',
    'a building with no District segment must compose to just the building name');
  console.assert(P._composeName('') === '',
    'no addr_en (every JD row) must compose to empty, so the caller falls back to name_zh');

  const root = document.querySelector('[data-pickup-picker]');
  console.assert(root, 'picker root must exist on the cart page');
  console.assert(getComputedStyle(root).overflowAnchor === 'none',
    'picker container needs overflow-anchor:none or Chrome will yank the page');

  await P.render();
  const opts = root.querySelectorAll('[data-pickup-district] option');
  console.assert(opts.length === 20,
    'expected 18 districts + Others + "all", got ' + opts.length);
  // Regression guard: the "all districts" option once silently fell back to the
  // (always-truthy) "District" translation instead of a dedicated key.
  console.assert(opts[0].value === '', 'first option must be the "all districts" placeholder');
  // Regression proof, not a tautology: render() BUILDS the option text from
  // pickup_all_districts, so comparing it to itself can never catch that global
  // resolving to the wrong source string. The shipped bug was pickup_all_districts
  // === the district label (both read "District") — assert they differ instead.
  console.assert(opts[0].textContent.trim().length > 0, 'all-districts option is empty');
  console.assert(opts[0].textContent !== (window.PXUTheme.translation || {}).pickup_district_label,
    'all-districts label must not equal the district label ' +
    '("' + opts[0].textContent + '") — that is the bug this guards against');

  // Carrier select: same "all" + real options shape as district.
  const csel = root.querySelector('[data-pickup-carrier-select]');
  console.assert(csel, 'carrier select must exist');
  const copts = csel.querySelectorAll('option');
  console.assert(copts.length === 4, 'expected 3 carriers + "all", got ' + copts.length);
  console.assert(copts[0].value === '', 'first carrier option must be the "all carriers" placeholder');
  console.assert(copts[0].textContent.trim().length > 0, 'all-carriers option is empty');
  console.assert(['SF', 'JD', 'CAINIAO'].every((k, i) => copts[i + 1].value === k),
    'carrier options must be SF, JD, CAINIAO in that order');

  // Carrier filter, and its AND-composition with district (Change 2). Counts are from
  // the committed indexes, not guessed.
  console.assert(P.filter(all, { district: '觀塘區', carrier: 'SF' }).length === 125, '觀塘區+SF wrong');
  console.assert(P.filter(all, { district: '觀塘區', carrier: 'JD' }).length === 78, '觀塘區+JD wrong');
  console.assert(P.filter(all, { district: '觀塘區', carrier: 'CAINIAO' }).length === 67, '觀塘區+CAINIAO wrong');
  console.assert(P.filter(all, { district: '沙田區', carrier: 'SF' }).length === 175, '沙田區+SF wrong');
  console.assert(P.filter(all, { district: '沙田區', carrier: 'CAINIAO' }).length === 83, '沙田區+CAINIAO wrong');
  console.assert(P.filter(all, { carrier: 'SF' }).length === 1637, 'SF total wrong');
  console.assert(P.filter(all, { carrier: 'JD' }).length === 1012, 'JD total wrong');
  console.assert(P.filter(all, { carrier: 'CAINIAO' }).length === 957, 'CAINIAO total wrong');
  console.assert(P.filter(all, { carrier: 'SF', text: 'zzzznomatch' }).length === 0,
    'carrier and text must AND, not OR');

  // Carrier-only selection (no district, no text) must paint, and must not leak.
  csel.value = 'CAINIAO';
  csel.dispatchEvent(new Event('change', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));
  const cRows = root.querySelectorAll('[data-pickup-row]');
  console.assert(cRows.length > 0, 'carrier-only selection must paint the list');
  console.assert(Array.from(cRows).every(li => li.querySelector('[data-pickup-carrier]').textContent === 'CAINIAO'),
    'carrier filter leaks other carriers');
  csel.value = '';
  csel.dispatchEvent(new Event('change', { bubbles: true }));

  // The 60-row cap and its truncation notice were unasserted. 觀塘區 holds 270 points in
  // the committed indexes, so this is stable data, not a live count that drifts.
  const sel = root.querySelector('[data-pickup-district]');
  console.assert(P.filter(all, { district: '觀塘區' }).length === 270,
    '觀塘區 should hold 270 points, got ' + P.filter(all, { district: '觀塘區' }).length);
  sel.value = '觀塘區';
  sel.dispatchEvent(new Event('change', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));
  const capped = root.querySelectorAll('[data-pickup-row]');
  console.assert(capped.length === 60, 'list must cap at 60 rows, got ' + capped.length);
  const notice = root.querySelectorAll('[data-pickup-truncated]');
  console.assert(notice.length === 1, 'exactly one truncation notice, got ' + notice.length);
  console.assert(notice[0] && notice[0].textContent.indexOf('270') > -1,
    'truncation notice must name the real total (270): "' +
    (notice[0] && notice[0].textContent) + '"');
  sel.value = '';
  sel.dispatchEvent(new Event('change', { bubbles: true }));

  const box = root.querySelector('[data-pickup-search]');
  box.value = '寶盈花園';
  box.dispatchEvent(new Event('input', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));
  const rows = root.querySelectorAll('[data-pickup-row]');
  console.assert(rows.length > 0 && rows.length < 50, 'search should narrow the list, got ' + rows.length);
  console.assert(rows[0].querySelector('[data-pickup-carrier]'), 'each row must show its carrier');
  console.assert(rows[0].querySelector('[data-pickup-code]').textContent === mall[0].code,
    'each row must display its point code (searched by code, so it must confirm the match)');

  let fired = null;
  document.addEventListener('pickup:selected', e => { fired = e.detail; }, { once: true });
  rows[0].querySelector('[data-pickup-choose]').click();
  await new Promise(r => setTimeout(r, 50));
  console.assert(fired && fired.code && fired.carrier, 'choosing a row must fire pickup:selected');

  console.log('pickup ui selftest ok');

  // The row click above now fires a REAL pickup:selected (Task 4 wires it to setPickup),
  // an unawaited async chain (attributes, then the address). Wait for it to settle before
  // the deterministic checks below, or that write races them on the same cart's addresses.
  await (HillmalayaPickup._pickupWrite || Promise.resolve());

  // Change 1: locale-aware name/address rendering. Force a locale rather than trust
  // whatever the browser happens to be on, so this assertion means the same thing on
  // every environment. Re-query from `document`, not the `root`/`sel`/`box`/`csel` above
  // — the write just awaited re-rendered .cart__form (same C1 class the district
  // regression guard below exists for), so those references are detached.
  const root2 = document.querySelector('[data-pickup-picker]');
  const sel3 = root2.querySelector('[data-pickup-district]');
  const csel3 = root2.querySelector('[data-pickup-carrier-select]');
  const box2 = root2.querySelector('[data-pickup-search]');
  const savedLocale = window.Shopify.locale;
  window.Shopify.locale = 'en';
  sel3.value = '';
  sel3.dispatchEvent(new Event('change', { bubbles: true }));
  box2.value = '852M';
  box2.dispatchEvent(new Event('input', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));
  const enRow = Array.from(root2.querySelectorAll('[data-pickup-row]'))
    .find(li => li.querySelector('[data-pickup-carrier]').textContent === 'SF');
  console.assert(enRow, 'expected to find the 852M SF test point');
  console.assert(enRow.querySelectorAll('small')[0].textContent.indexOf('Bonham Strand') > -1,
    'en locale must render addr_en for a row that has one');

  const jdPoint = all.find(p => p.carrier === 'JD');
  csel3.value = 'JD';
  csel3.dispatchEvent(new Event('change', { bubbles: true }));
  box2.value = jdPoint.code;
  box2.dispatchEvent(new Event('input', { bubbles: true }));
  await new Promise(r => setTimeout(r, 50));
  const jdRow = Array.from(root2.querySelectorAll('[data-pickup-row]'))
    .find(li => li.querySelector('strong').textContent === jdPoint.name_zh);
  console.assert(jdRow, 'expected to find the JD test point by code+carrier filter');
  // Explicit, not just implied by the find() above: every JD row has no addr_en, so
  // _composeName('') must return '' and the name must fall back to name_zh even on en.
  console.assert(jdRow.querySelector('strong').textContent === jdPoint.name_zh,
    'a JD row (no addr_en) must fall back to name_zh on en locale, not an empty/partial name');
  console.assert(jdRow.querySelectorAll('small')[0].textContent === jdPoint.addr_zh,
    'JD row (no addr_en) must fall back to addr_zh on en locale');

  window.Shopify.locale = savedLocale;
  csel3.value = '';
  csel3.dispatchEvent(new Event('change', { bubbles: true }));
  box2.value = '';
  box2.dispatchEvent(new Event('input', { bubbles: true }));

  const pt = { carrier: 'SF', code: '852M', name_zh: '上環文樂商廈順豐站',
               addr_zh: '香港島中西區文咸東街91號', district_zh: '中西區' };
  await new Promise(res => window.PXUTheme.jsAjaxCart.setPickup(pt, res));
  const c1 = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
  console.assert(c1.attributes.pickup_carrier === 'SF', 'pickup_carrier not written');
  console.assert(c1.attributes.pickup_code === '852M', 'pickup_code not written');
  console.assert(c1.attributes.pickup_name === '上環文樂商廈順豐站', 'pickup_name not written');
  console.assert(c1.attributes.pickup_region === '中西區', 'pickup_region not written');
  console.assert(c1.attributes.pickup_address === '香港島中西區文咸東街91號', 'pickup_address not written');
  console.assert((await P._readAddresses()).length === 1, 'address not set after setPickup');

  // C1 regression guard. Every OTHER DOM assertion above runs before the first cartOp,
  // so `root` is only ever read from a page that has not been re-rendered — which is
  // exactly why this class went unseen. cartOp() refetches /cart and replaceWith()s
  // .cart__form on every mutation, and the picker lives inside it: re-query from
  // `document` (the old `root` is detached) and check the dropdown survived.
  const sel2 = document.querySelector('[data-pickup-district]');
  console.assert(sel2 && sel2.options.length === 19,
    'district dropdown was emptied by a cart re-render and nothing repopulated it — got ' +
    (sel2 ? sel2.options.length : 'no select'));
  const csel2 = document.querySelector('[data-pickup-carrier-select]');
  console.assert(csel2 && csel2.options.length === 4,
    'carrier dropdown was emptied by a cart re-render and nothing repopulated it — got ' +
    (csel2 ? csel2.options.length : 'no select'));

  await new Promise(res => window.PXUTheme.jsAjaxCart.clearPickup(res));
  const c2 = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
  console.assert(!c2.attributes.pickup_carrier, 'pickup_carrier not cleared');
  console.assert(!c2.attributes.pickup_code, 'pickup_code not cleared');
  // THE one that matters: clearing attributes alone leaves the address behind.
  console.assert((await P._readAddresses()).filter(x => x.oneTimeUse).length === 0,
    'clearPickup left a stale pickup address on the cart');

  console.log('pickup cart-write selftest ok');
};

// Districts read off the SEED markup instead of the index. The seed carries every
// district's Chinese key, English name and true count, so the finder's filters are fully
// usable before a single byte of the 1.22 MB index is fetched — which is the only reason
// deferring it is not just moving the wait to the first click. Empty everywhere else (the
// cart renders no seed), and the caller falls back to the index.
HillmalayaPickup._seedDistricts = function () {
  return Array.prototype.map.call(
    document.querySelectorAll('[data-pickup-seed-district]'),
    el => ({
      zh: el.getAttribute('data-pickup-seed-district'),
      en: el.getAttribute('data-pickup-seed-name'),
      // Absent on the 18 real districts, where the Chinese name IS the filter key. Only
      // the "Others" bucket carries it, because its label is not a district name.
      key: el.getAttribute('data-pickup-seed-key') || el.getAttribute('data-pickup-seed-district'),
      count: parseInt(el.getAttribute('data-pickup-seed-count'), 10) || 0
    }));
};

// Both selects are guarded on being empty, so this is idempotent: the deferred page fills
// them from the seed, and the later index-backed render leaves them alone. The two agree
// by construction — the seed is generated from these same three assets.
HillmalayaPickup._fillFilters = function (root, districts) {
    const sel = root.querySelector('[data-pickup-district]');
    // Clear the deferred-load placeholder first. This function's guard is "the select is
    // empty", so leaving a lone placeholder in place would block the real districts
    // forever — the select would keep saying "All districts" and nothing else.
    if (sel) {
      const hint = sel.querySelector('[data-pickup-loading]');
      if (hint) hint.remove();
    }
    if (sel && !sel.options.length) {
      sel.appendChild(new Option((window.PXUTheme.translation || {}).pickup_all_districts || 'All districts', ''));
      // district_en is populated on every row that resolves a district (18/18, 0 missing),
      // so an English-reading buyer gets English district names on the one surface where
      // the data actually exists. The VALUE stays Chinese — it is the filter key.
      const en = !/^zh/i.test(window.Shopify.locale || '');
      districts.forEach(d => {
        sel.appendChild(new Option(((en && d.en) || d.zh) + ' (' + d.count + ')', d.key || d.zh));
      });
    }
    const csel = root.querySelector('[data-pickup-carrier-select]');
    if (csel && !csel.options.length) {
      csel.appendChild(new Option((window.PXUTheme.translation || {}).pickup_all_carriers || 'All carriers', ''));
      CARRIERS.forEach(c => csel.appendChild(new Option(c.label, c.key)));
    }
};

// Between the toggle being ticked and the index landing there are a couple of seconds on a
// slow connection, and the buyer is looking at a select they just revealed. Show the
// existing "all districts" label rather than an empty control: it is the truth (no filter
// applied yet) and it needs no new copy in 11 locale files. _fillFilters removes it.
HillmalayaPickup._loadingHint = function (root, on) {
  const sel = root && root.querySelector('[data-pickup-district]');
  if (!sel) return;
  const existing = sel.querySelector('[data-pickup-loading]');
  if (on) {
    if (!sel.options.length && !existing) {
      const o = new Option((window.PXUTheme.translation || {}).pickup_all_districts || 'All districts', '');
      o.setAttribute('data-pickup-loading', '');
      sel.appendChild(o);
    }
  } else if (existing) {
    existing.remove();
  }
};

HillmalayaPickup.render = function () {
  const root = document.querySelector('[data-pickup-picker]');
  if (!root) return Promise.resolve();
  // The finder page ships every district server-rendered, so the index is not what the
  // visitor is waiting for on arrival. Fill the filters from that markup and fetch nothing
  // until they actually filter. The cart has no seed, and keeps its filters hidden until
  // the toggle is on, so it defers with nothing to fill.
  //
  // ⚠️ A buyer arriving from the finder with a point in localStorage is the ONE case that
  // needs the index up front: _offerPending resolves it against the live rows and must
  // never offer a point that has left the index. Rare (they used the finder), and the
  // alternative is silently dropping that offer.
  if (root.hasAttribute('data-pickup-defer') && !_points && !HillmalayaPickup._readPending()) {
    const seeded = HillmalayaPickup._seedDistricts();
    // Fill ONLY from a real seed. _fillFilters is guarded on the select being empty, so
    // filling it with a lone placeholder here would permanently block the index-backed
    // districts from ever landing.
    if (seeded.length) HillmalayaPickup._fillFilters(root, seeded);
    return Promise.resolve();
  }
  return HillmalayaPickup.load().then(all => {
    HillmalayaPickup._fillFilters(root, HillmalayaPickup.districts(all));
    HillmalayaPickup._offerPending(root, all);
    HillmalayaPickup._paint();
  });
};

// One entry point for every filter interaction. On the deferred page the first one is what
// pays for the index; everywhere else it is just a repaint. load() dedupes concurrent
// callers, so a fast typist cannot start two fetches.
HillmalayaPickup._paintOrLoad = function () {
  // Always a promise: _paint() returns undefined, and callers chain on this to know when
  // the index has landed. One contract here beats a guard at every call site.
  if (_points) return Promise.resolve(HillmalayaPickup._paint());
  const root = document.querySelector('[data-pickup-picker]');
  if (root) root.removeAttribute('data-pickup-defer');
  return HillmalayaPickup.render();
};

// What happens when someone picks a point, in ONE place. The picker's own rows and the
// server-rendered district rows both call it, so the two surfaces cannot drift on the thing
// that actually matters — which cart the point lands in.
// The name a row shows, in ONE place. Both renderers of this list call it — the picker's
// painted rows and the server-rendered district rows — because they have now drifted on
// three separate fields (the address, the locale, this) and each drift shipped.
//
// name_en is empty on every row today; compose an English label from addr_en only on a
// non-zh locale, and only as a last resort before the Chinese name. Strongest evidence
// first: a published English name, else the Chinese name with just the carrier's generic
// token swapped, else one composed from this row's OWN English address, else the Chinese
// name untouched. Nothing here is invented — every rung derives from this specific point.
// --- near me -------------------------------------------------------------------------
// Where the buyer said they are, for as long as the page lives. Never stored, never sent.
let _origin = null;

// Hong Kong's bounding box, the same one als_gate.py uses to reject an ALS match that
// landed outside the territory. A buyer abroad gets a "nearest" point 8,000 km away and a
// distance sort that means nothing, so that case is named rather than ranked.
const HK_BOX = { lat: [22.10, 22.60], lng: [113.80, 114.50] };

// Haversine, not the equirectangular approximation: on 3,606 rows the saving is
// unmeasurable and this decides which point a buyer walks to.
HillmalayaPickup._haversine = function (lat1, lng1, lat2, lng2) {
  const R = 6371, rad = Math.PI / 180;
  const dLat = (lat2 - lat1) * rad, dLng = (lng2 - lng1) * rad;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2)
    + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
};

// ⚠️ NEVER MORE PRECISION THAN THE INPUT DESERVES. A desktop position is a Wi-Fi lookup that
// can be a kilometre out, so "1.23 km" claims an accuracy we do not have. Under a kilometre
// round to 50 m; above it, one decimal.
HillmalayaPickup._formatDistance = function (km) {
  const t = window.PXUTheme.translation || {};
  if (km < 1) {
    const m = Math.max(50, Math.round(km * 1000 / 50) * 50);
    return (t.pickup_metres || '__N__ m away').replace('__N__', m);
  }
  return (t.pickup_km || '__N__ km away').replace('__N__', km.toFixed(1));
};

HillmalayaPickup._inHK = function (lat, lng) {
  return lat >= HK_BOX.lat[0] && lat <= HK_BOX.lat[1]
      && lng >= HK_BOX.lng[0] && lng <= HK_BOX.lng[1];
};

HillmalayaPickup._geoStatus = function (msg, busy) {
  const root = document.querySelector('[data-pickup-picker]');
  const el = root && root.querySelector('[data-pickup-geo-status]');
  // BOTH origin buttons, not just Near me: the address lookup shares this status line, so a
  // search left the address button live and clickable while its own request was in flight.
  const btn = root && root.querySelector('[data-pickup-near]');
  const addr = root && root.querySelector('[data-pickup-addr-go]');
  if (btn) btn.disabled = !!busy;
  if (addr) addr.disabled = !!busy;
  if (!el) return;
  el.textContent = msg || '';
  el.hidden = !msg;
};

HillmalayaPickup._nearMe = function () {
  const t = window.PXUTheme.translation || {};
  const root = document.querySelector('[data-pickup-picker]');
  if (!navigator.geolocation) {
    HillmalayaPickup._geoStatus(t.pickup_geo_unavailable || 'Location is not available in this browser.');
    return;
  }
  HillmalayaPickup._geoStatus(t.pickup_geo_locating || 'Finding your location...', true);
  navigator.geolocation.getCurrentPosition(function (pos) {
    const c = pos.coords;
    if (!HillmalayaPickup._inHK(c.latitude, c.longitude)) {
      // Ranking 3,606 Hong Kong points by distance from another country is arithmetic
      // with no meaning. Say so and leave the district filter working.
      _origin = null;
      HillmalayaPickup._geoStatus(t.pickup_geo_outside || 'You appear to be outside Hong Kong, so nearby points cannot be ranked. Browse by district instead.');
      HillmalayaPickup._paint();
      return;
    }
    _origin = { lat: c.latitude, lng: c.longitude, accuracy: c.accuracy };
    // A distance sort inside one district is a contradiction the buyer did not ask for.
    // The CARRIER filter is kept: "nearest SF point" is a real question.
    const ds = root && root.querySelector('[data-pickup-district]');
    if (ds) ds.value = '';
    const search = root && root.querySelector('[data-pickup-search]');
    if (search) search.value = '';
    // Above ~2 km of reported accuracy the ordering is noise. Show it, and say so.
    HillmalayaPickup._geoStatus(
      c.accuracy > 2000
        ? (t.pickup_geo_approx || 'Your location is approximate, so this order is a rough guide.')
        : (t.pickup_geo_found || 'Sorted by distance from you.'));
    HillmalayaPickup._paintOrLoad();
  }, function (err) {
    _origin = null;
    HillmalayaPickup._paint();
    // ⚠️ A denial is STICKY: every later tap rejects instantly with no prompt, which reads
    // as a broken button. Say the permission is blocked and where to change it, never
    // "try again".
    HillmalayaPickup._geoStatus(
      err.code === 1 ? (t.pickup_geo_denied || 'Location permission is blocked. Allow it in your browser settings to use this.')
        : err.code === 3 ? (t.pickup_geo_timeout || 'Finding your location took too long. Browse by district instead.')
        : (t.pickup_geo_unavailable || 'Your location is not available. Browse by district instead.'));
  }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 });
};

// --- near an address ------------------------------------------------------------------
// Same distance sort, different origin: a coordinate geocoded from what the buyer typed
// rather than from the device. Everything downstream (_haversine, the km labels, the 20-row
// cap, the carrier filter) is the near-me path untouched — only _origin is set differently.
//
// It reads [data-pickup-search], the SAME box as the text filter (Wing, 2026-09-16 — this
// shipped with a field of its own and he reversed it). Matching a point by its address text
// and ranking points around a coordinate are the same question asked two ways, and the
// coordinate answers more of it: a street none of our points is named after matches nothing
// by text and has points around it regardless.
//
// ⚠️ TWO GOV APIs, BOTH BROWSER-CALLABLE (Access-Control-Allow-Origin: * on each, verified
// 2026-09-16), so there is no proxy and no key:
//   CSDI locationSearch  q -> [{nameZH,nameEN,addressZH,addressEN,districtZH,districtEN,x,y}]
//   Lands Dept transform HK80 easting/northing -> {wgsLat, wgsLong}
// CSDI rather than ALS because it answers with the name the buyer TYPED — "Landmark East",
// "Langham Place" — which is what makes the candidate list recognisable. ALS is
// address-oriented and answers a landmark with its constituent buildings (Landmark East ->
// AIA Kowloon Tower / AXA Tower): correct, and unreadable as a list of choices.
// Cost of that choice is HK80 coordinates, hence the transform — which cross-checks against
// ALS to about a metre on the same building (22.312213905/114.224023538 vs 22.31221/114.22402).
const GEO_TIMEOUT = 8000;
const ADDR_MAX_HITS = 6;
// Supersession counter. A slow first search must never repaint over a newer one's results —
// same shape as jsCartUpsell's _gen, and the reason is the same: the request that finishes
// last is not necessarily the one the buyer is waiting for.
let _addrGen = 0;

HillmalayaPickup._fetchJSON = function (url) {
  // AbortSignal.timeout is not in every browser this theme serves, so the controller is
  // driven by hand. Without a deadline a hung gov endpoint leaves the button disabled
  // forever, which reads as a dead control.
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), GEO_TIMEOUT) : null;
  return fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
    .then(r => {
      if (!r.ok) throw new Error('http ' + r.status);
      return r.json();
    })
    .finally(() => { if (timer) clearTimeout(timer); });
};

HillmalayaPickup._geocode = function (q) {
  const url = 'https://www.map.gov.hk/gs/api/v1.0.0/locationSearch?q=' + encodeURIComponent(q);
  return HillmalayaPickup._fetchJSON(url).then(rows => (rows || [])
    // A row with no coordinate cannot be an origin. Dropping it here keeps the candidate
    // list honest: everything shown is tappable.
    .filter(r => typeof r.x === 'number' && typeof r.y === 'number')
    .slice(0, ADDR_MAX_HITS));
};

// HK80 grid -> WGS84, for the ONE candidate the buyer picked.
HillmalayaPickup._toWgs = function (x, y) {
  const url = 'https://www.geodetic.gov.hk/transform/v2/?inSys=hkgrid&outSys=wgsgeog&e='
    + encodeURIComponent(x) + '&n=' + encodeURIComponent(y);
  return HillmalayaPickup._fetchJSON(url).then(d => {
    const lat = parseFloat(d && d.wgsLat), lng = parseFloat(d && d.wgsLong);
    if (!isFinite(lat) || !isFinite(lng)) throw new Error('no coordinate');
    return { lat: lat, lng: lng };
  });
};

// What the buyer reads on a candidate row, in their own language.
// ⚠️ CSDI joins alternate names for one place with a PIPE — "8 THISTLE STREET|999 CANTON
// ROAD" is a single row, not two. Left raw it reads as a broken string, so it is rendered
// as a separator; the runs of whitespace are the API's own ("巧明街   105號").
HillmalayaPickup._addrLabel = function (r, zh) {
  const tidy = s => (s || '').replace(/\s*\|\s*/g, ' / ').replace(/\s+/g, ' ').trim();
  const name = tidy(zh ? r.nameZH : r.nameEN) || tidy(r.nameEN) || tidy(r.nameZH);
  const addr = tidy(zh ? r.addressZH : r.addressEN) || tidy(r.addressEN) || tidy(r.addressZH);
  const dist = tidy(zh ? r.districtZH : r.districtEN);
  return { name: name || addr, sub: [addr, dist].filter(Boolean).join(', ') };
};

HillmalayaPickup._addrHits = function (rows) {
  const root = document.querySelector('[data-pickup-picker]');
  const box = root && root.querySelector('[data-pickup-addr-hits]');
  if (!box) return;
  const zh = /^zh/i.test(window.Shopify.locale || '');
  box.innerHTML = '';
  (rows || []).forEach(r => {
    const label = HillmalayaPickup._addrLabel(r, zh);
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    // textContent throughout: these strings are third-party API output rendered into our
    // page, so they are never trusted as markup.
    btn.appendChild(document.createTextNode(label.name));
    if (label.sub) {
      const sub = document.createElement('span');
      sub.className = 'pickup-picker__addr-sub';
      sub.textContent = label.sub;
      btn.appendChild(sub);
    }
    btn.addEventListener('click', () => HillmalayaPickup._useAddr(r, label.name));
    li.appendChild(btn);
    box.appendChild(li);
  });
  box.hidden = !(rows && rows.length);
};

HillmalayaPickup._addrSearch = function () {
  const t = window.PXUTheme.translation || {};
  const root = document.querySelector('[data-pickup-picker]');
  // THE SAME BOX the text filter reads. Nothing here consumes the value — the filter keeps
  // working on it until a candidate is tapped — so the two readings never fight for it.
  const input = root && root.querySelector('[data-pickup-search]');
  const q = input ? input.value.trim() : '';
  if (!q) return;
  const gen = ++_addrGen;
  HillmalayaPickup._addrHits([]);
  HillmalayaPickup._geoStatus(t.pickup_addr_searching || 'Looking up that address...', true);
  HillmalayaPickup._geocode(q).then(rows => {
    if (gen !== _addrGen) return;
    if (!rows.length) {
      HillmalayaPickup._geoStatus(t.pickup_addr_none
        || 'No place found for that. Try a street number, a building name, or browse by district.');
      return;
    }
    HillmalayaPickup._addrHits(rows);
    HillmalayaPickup._geoStatus(t.pickup_addr_pick || 'Which one did you mean?');
  }).catch(() => {
    if (gen !== _addrGen) return;
    HillmalayaPickup._addrHits([]);
    HillmalayaPickup._geoStatus(t.pickup_addr_error
      || 'The address lookup is unavailable right now. Browse by district instead.');
  });
};

HillmalayaPickup._useAddr = function (row, name) {
  const t = window.PXUTheme.translation || {};
  const root = document.querySelector('[data-pickup-picker]');
  const gen = ++_addrGen;
  HillmalayaPickup._geoStatus(t.pickup_addr_searching || 'Looking up that address...', true);
  HillmalayaPickup._toWgs(row.x, row.y).then(c => {
    if (gen !== _addrGen) return;
    // The transform is the one step that can put the origin somewhere impossible — a wrong
    // input system, a swapped easting/northing. HK80 metres and WGS84 degrees are six orders
    // of magnitude apart, so a failure here is never subtle, and this refuses rather than
    // ranking 3,606 points from the South Atlantic.
    if (!HillmalayaPickup._inHK(c.lat, c.lng)) {
      _origin = null;
      HillmalayaPickup._geoStatus(t.pickup_addr_error
        || 'The address lookup is unavailable right now. Browse by district instead.');
      return;
    }
    _origin = { lat: c.lat, lng: c.lng };
    HillmalayaPickup._addrHits([]);
    // Same reasoning as _nearMe: a distance sort INSIDE one district is a contradiction, and
    // the point-text filter would hide the very rows the ranking just ordered. The CARRIER
    // filter stays — "nearest SF point to my office" is a real question (Wing, 2026-09-16).
    // ⚠️ Clearing the search box is now clearing what the buyer TYPED, since it is the same
    // box — so the status line below is the only thing left saying which place this list is
    // ranked from, and it must name it. Leaving the text in would filter the ranked rows
    // down to the ones whose own name contains the street, which is usually none.
    const ds = root && root.querySelector('[data-pickup-district]');
    if (ds) ds.value = '';
    const search = root && root.querySelector('[data-pickup-search]');
    if (search) search.value = '';
    HillmalayaPickup._geoStatus(
      (t.pickup_addr_from || 'Sorted by distance from __N__.').replace('__N__', name));
    HillmalayaPickup._paintOrLoad();
  }).catch(() => {
    if (gen !== _addrGen) return;
    HillmalayaPickup._geoStatus(t.pickup_addr_error
      || 'The address lookup is unavailable right now. Browse by district instead.');
  });
};

HillmalayaPickup._displayName = function (p, zh) {
  if (zh) return p.name_zh;
  // The English PLACE, best source first. Deliberately the building only, not the composed
  // "Building, Locality" — the locality would land mid-name ("Man Lok Building, Sheung Wan
  // SF Station") and it already appears on the address line directly below.
  const placeEn = p.name_en || HillmalayaPickup._buildingOf(p.addr_en);
  // Carrier-token swap comes FIRST: it is the only rung that keeps the facility type, and
  // dropping that was the 港佳商業大廈京東服務站 defect.
  return HillmalayaPickup._carrierLabel(p.name_zh, placeEn)
      || p.name_en
      || HillmalayaPickup._composeName(p.addr_en)
      || p.name_zh;
};

HillmalayaPickup._applyChoice = function (point, btn) {
  const t = window.PXUTheme.translation || {};
  const root = document.querySelector('[data-pickup-picker]');
  const page = root && root.getAttribute('data-pickup-mode') === 'page';
  if (!page) {
    document.dispatchEvent(new CustomEvent('pickup:selected', { detail: point }));
    return;
  }
  // Finder: remember it. Confirm in place so the row does not reflow under the pointer, and
  // say plainly when storage refused rather than claiming a save that did not happen.
  const ok = HillmalayaPickup._writePending(point);
  const done = document.createElement('span');
  done.setAttribute('data-pickup-saved', '');
  done.textContent = ok ? (t.pickup_saved || 'Saved')
                        : (t.pickup_save_failed || 'Could not save');
  btn.replaceWith(done);
};

// ⚠️ THE SEED ROWS SHIPPED LOOKING EXACTLY LIKE THE PICKER'S AND CARRYING NO ACTION. They are
// server-rendered for crawlers, so they hold a code and no point object — and a code is all
// they need, because the index can resolve it. Buttons are added HERE rather than emitted by
// build.py: 216 of them cost bytes on the store's heaviest page and mean nothing to a
// crawler, and only the client knows whether a cart exists to use them.
HillmalayaPickup._wireSeedRows = function () {
  const root = document.querySelector('[data-pickup-picker]');
  // Same rule as the picker's rows: no cart that could ever use the point, no button.
  if (!root || !root.hasAttribute('data-pickup-cart-ok')) return;
  const label = (window.PXUTheme.translation || {}).pickup_choose || 'Choose';
  // ⚠️ The seed is server-rendered ONCE and cannot know the reader's language, so on a
  // non-zh locale its Chinese names disagreed with the English ones the picker composed for
  // the very same rows. Everything the ladder needs — the Chinese name and this row's own
  // English address — is already in this markup, so it runs here with no index fetch.
  const zh = /^zh/i.test(window.Shopify.locale || '');
  Array.prototype.forEach.call(document.querySelectorAll('[data-pickup-seed-code]'), li => {
    if (!zh) {
      const strong = li.querySelector('strong');
      const addrEl = li.querySelector('span');
      const composed = HillmalayaPickup._displayName(
        { name_zh: strong.textContent, name_en: '', addr_en: addrEl ? addrEl.textContent : '' },
        false);
      // Only ever replace with something; a row whose address had no English falls back to
      // the Chinese name the ladder returns, which is what it already shows.
      if (composed) strong.textContent = composed;
    }
    if (li.querySelector('[data-pickup-choose]')) return;
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('data-pickup-choose', '');
    btn.textContent = label;
    btn.addEventListener('click', () => {
      const code = li.getAttribute('data-pickup-seed-code');
      btn.disabled = true;
      // The index is deferred on this page, so the first seed choose is what pays for it.
      HillmalayaPickup.load().then(all => {
        const point = all.filter(x => x.code === code)[0];
        btn.disabled = false;
        // A seed row whose code has left the index must not silently do nothing, and must
        // not write a point we cannot resolve. Say so and leave the row readable.
        if (!point) {
          btn.textContent = (window.PXUTheme.translation || {}).pickup_save_failed
            || 'Could not save';
          return;
        }
        HillmalayaPickup._applyChoice(point, btn);
      }).catch(() => {
        btn.disabled = false;
        btn.textContent = label;
      });
    });
    li.appendChild(btn);
  });
};

HillmalayaPickup._paint = function () {
  const root = document.querySelector('[data-pickup-picker]');
  if (!root || !_points) return;   // a keystroke can land before load() resolves
  const list = root.querySelector('[data-pickup-list]');
  const district = root.querySelector('[data-pickup-district]').value;
  const carrier = root.querySelector('[data-pickup-carrier-select]').value;
  const text = root.querySelector('[data-pickup-search]').value;
  // Nothing chosen yet and no filter applied: don't paint 3,606 rows. A known position IS
  // a filter — without it in this test, tapping Near me paints nothing.
  if (!district && !carrier && !text.trim() && !_origin) { list.innerHTML = ''; return; }
  let matched = HillmalayaPickup.filter(_points, { district: district, carrier: carrier, text: text });
  let unmapped = 0;
  if (_origin) {
    // ⚠️ SOME POINTS HAVE NO COORDINATE AND CANNOT BE RANKED — 276 of 3,606 (7.7%) as of
    // 2026-09-01, down from 29% when this was written. Dropping them silently would shrink
    // the catalogue with nothing on screen saying so, so they are counted here and named
    // below the list — the same rule as the 60-row truncation note. The share will keep
    // falling as the manual sheet is worked, and the note costs nothing when it reaches 0.
    const before = matched.length;
    matched = matched.filter(p => typeof p.lat === 'number' && typeof p.lng === 'number');
    unmapped = before - matched.length;
    matched.forEach(p => {
      p._km = HillmalayaPickup._haversine(_origin.lat, _origin.lng, p.lat, p.lng);
    });
    matched.sort((a, b) => a._km - b._km);
  }
  const hits = matched.slice(0, _origin ? 20 : 60);
  list.innerHTML = '';
  // ⚠️ THE ONE SCREEN THAT MAKES THE MERGED BOX DISCOVERABLE. A street none of our points is
  // named after — 駿業里, the case this feature exists for — filters to nothing, and before
  // this the list simply went blank: a dead end that looks like "there are no points here"
  // when there are several a minute's walk away. So the empty state names the way out.
  // The BUTTON'S OWN TEXT is read out of the DOM rather than a second locale key: the label
  // is already quoted in three places across two systems (see .claude/rules/pickup-points.md)
  // and a fourth copy that goes stale silently is exactly that entry's failure.
  if (!hits.length && !_origin && text.trim()) {
    const t = window.PXUTheme.translation || {};
    const btn = root.querySelector('[data-pickup-addr-go]');
    const note = document.createElement('li');
    note.setAttribute('data-pickup-empty', '');
    note.textContent = (t.pickup_no_match
      || 'Nothing in the list matches that. Tap __BTN__ to rank points near it instead.')
      .replace('__BTN__', (btn && btn.textContent.trim()) || '');
    list.appendChild(note);
    return;
  }
  // On /zh everything stays Chinese; elsewhere prefer the English field and fall back
  // to Chinese where a carrier (JD, and some SF/Cainiao rows) never published one.
  const zh = /^zh/i.test(window.Shopify.locale || '');
  const page = root.getAttribute('data-pickup-mode') === 'page';
  const cartOk = root.hasAttribute('data-pickup-cart-ok');
  hits.forEach(p => {
    const li = document.createElement('li');
    li.setAttribute('data-pickup-row', '');
    li.innerHTML =
      '<span data-pickup-carrier></span>' +
      '<span><strong></strong> <span data-pickup-code></span> <span data-pickup-distance></span>' +
      '<br><small></small><br><small></small> <a data-pickup-directions target="_blank" rel="noopener"></a></span>' +
      '<button type="button" data-pickup-choose></button>';
    // p.carrier/p.code/p.addr_*/p.hours are all scraped/derived data — textContent,
    // never innerHTML, so none of it can ever become markup.
    li.querySelector('[data-pickup-carrier]').textContent = p.carrier;
    // A code (e.g. "852M") is language-neutral and is what filter() already searches
    // on — show it in both locales so a shopper who searched by code can confirm the
    // match, visually secondary to the name (it's a reference, not the label).
    li.querySelector('[data-pickup-code]').textContent = p.code || '';
    li.querySelector('strong').textContent = HillmalayaPickup._displayName(p, zh);
    li.querySelectorAll('small')[0].textContent = zh ? p.addr_zh : (p.addr_en || p.addr_zh);
    li.querySelectorAll('small')[1].textContent =
      zh ? (p.hours || '') : HillmalayaPickup._translateHours(p.hours || '');
    if (_origin && typeof p._km === 'number') {
      li.querySelector('[data-pickup-distance]').textContent =
        HillmalayaPickup._formatDistance(p._km);
      // Straight-line distance only orders the shortlist; Hong Kong's harbour and hills make
      // travel time a different question, and this link is how the buyer asks it. A plain
      // URL — no API key, no quota, no script — that opens the native app on a phone.
      const a = li.querySelector('[data-pickup-directions]');
      a.href = 'https://www.google.com/maps/dir/?api=1&destination='
        + encodeURIComponent(p.lat + ',' + p.lng);
      a.textContent = (window.PXUTheme.translation || {}).pickup_directions || 'Directions';
    }
    const btn = li.querySelector('[data-pickup-choose]');
    if (page && !cartOk) {
      // The finder outside Hong Kong: the directory is still worth reading, but there is
      // no cart that could ever use the point. An action that cannot complete is worse
      // than no action, so the row simply carries no button.
      btn.remove();
    } else {
      btn.textContent = (window.PXUTheme.translation || {}).pickup_choose || 'Choose';
      btn.addEventListener('click', () => HillmalayaPickup._applyChoice(p, btn));
    }
    list.appendChild(li);
  });
  // The list is silently capped at 60 while the district <option> shows the true
  // count — say so, or a shopper reading "觀塘區 (312)" sees 60 rows with nothing
  // telling them 252 are missing.
  if (unmapped) {
    const miss = document.createElement('li');
    miss.setAttribute('data-pickup-truncated', '');
    const tm = (window.PXUTheme.translation || {}).pickup_unmapped
      || '__COUNT__ more points here have no mapped location - browse by district to see them.';
    miss.textContent = tm.replace('__COUNT__', unmapped);
    list.appendChild(miss);
  }
  // Deliberately NOT while sorting by distance: the nearest 20 is the complete answer to
  // "what is near me", and "Showing 20 of 2559" would invite a buyer to look for more.
  if (!_origin && matched.length > hits.length) {
    const note = document.createElement('li');
    note.setAttribute('data-pickup-truncated', '');
    const tmpl = (window.PXUTheme.translation || {}).pickup_truncated ||
      'Showing __SHOWN__ of __TOTAL__ — narrow your search to see more.';
    note.textContent = tmpl.replace('__SHOWN__', hits.length).replace('__TOTAL__', matched.length);
    list.appendChild(note);
  }
};

document.addEventListener('input', e => {
  if (e.target.matches('[data-pickup-search]')) HillmalayaPickup._paintOrLoad();
});
// Enter in the search box runs the ADDRESS search, not the text filter — the filter already
// ran on every keystroke, so Enter has nothing else to mean, and it is the key a buyer
// presses after typing a street the point list does not match.
// ⚠️ CAPTURE PHASE, and that is not a style choice — ON THE CART PAGE SOMETHING CALLS
// stopPropagation() ON keydown, so a document-level BUBBLE listener never fires there.
// Measured on a preview theme 2026-09-16 with one counter per phase and a real key press:
// capture 1, bubble 0. The finder page has no such handler, so the bubble version worked
// there and looked correct — the two surfaces disagreeing is what exposed it.
// ⚠️ preventDefault is equally load-bearing: this input sits INSIDE #cart_form (verified),
// so the implicit submission Enter triggers in a form is a trip to checkout.
document.addEventListener('keydown', e => {
  if (e.key === 'Enter' && e.target.matches && e.target.matches('[data-pickup-search]')) {
    e.preventDefault();
    HillmalayaPickup._addrSearch();
  }
}, true);
document.addEventListener('change', e => {
  if (e.target.matches('[data-pickup-district]')) HillmalayaPickup._paintOrLoad();
  if (e.target.matches('[data-pickup-carrier-select]')) HillmalayaPickup._paintOrLoad();
  if (e.target.matches('[data-pickup-enable]')) {
    if (!e.target.checked) document.dispatchEvent(new CustomEvent('pickup:cleared'));
    else {
      // The tick is the intent signal, so it is what fetches the index — _paintOrLoad,
      // not render, because render would honour data-pickup-defer and fetch nothing.
      const root = e.target.closest('[data-pickup-picker]');
      const browse = root && root.querySelector('[data-pickup-browse]');
      if (browse) browse.hidden = false;
      HillmalayaPickup._loadingHint(root, true);
      HillmalayaPickup._paintOrLoad().then(() => HillmalayaPickup._loadingHint(root, false));
    }
  }
});
document.addEventListener('click', e => {
  // ⚠️ closest, NOT matches: Near me now contains an icon span, so a tap on the pin — the
  // biggest target in the button — has e.target as the span and matches() is false. The
  // button would be dead exactly where it looks most tappable.
  if (e.target.closest && e.target.closest('[data-pickup-near]')) {
    // ⚠️ Only ever from a tap. An unsolicited location prompt on page load is hostile and
    // Chrome demotes sites that raise one without a gesture.
    HillmalayaPickup._nearMe();
    return;
  }
  if (e.target.matches('[data-pickup-addr-go]')) {
    HillmalayaPickup._addrSearch();
    return;
  }
  // A seed district hands its full list to the picker rather than expanding in place:
  // one list, one set of filters, and the 250 rows it would otherwise have to render are
  // already the picker's job.
  const all = e.target.closest && e.target.closest('[data-pickup-seed-all]');
  if (all) {
    const box = all.closest('[data-pickup-seed-district]');
    const root = document.querySelector('[data-pickup-picker]');
    if (box && root) {
      // The KEY, not the label: they are the same string for the 18 real districts and
      // differ for "Others". Assigning a value no <option> carries silently resolves the
      // select to '' — i.e. "all districts" — so this would quietly show everything.
      root.querySelector('[data-pickup-district]').value =
        box.getAttribute('data-pickup-seed-key') ||
        box.getAttribute('data-pickup-seed-district');
      root.querySelector('[data-pickup-carrier-select]').value = '';
      root.querySelector('[data-pickup-search]').value = '';
      HillmalayaPickup._paintOrLoad();
      root.scrollIntoView({
        block: 'start',
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto' : 'smooth'
      });
    }
    return;
  }
  if (e.target.matches('[data-pickup-pending-use]')) {
    const box = e.target.closest('[data-pickup-pending]');
    box.hidden = true;
    HillmalayaPickup._writePending(null);
    // The same event the picker's own rows fire, so the write path has exactly one
    // implementation and this cannot drift from choosing a row by hand.
    document.dispatchEvent(new CustomEvent('pickup:selected', { detail: box._pickupPoint }));
    return;
  }
  if (e.target.matches('[data-pickup-pending-dismiss]')) {
    e.target.closest('[data-pickup-pending]').hidden = true;
    HillmalayaPickup._writePending(null);
    return;
  }
  if (e.target.matches('[data-pickup-change]')) {
    const root = document.querySelector('[data-pickup-picker]');
    root.querySelector('[data-pickup-chosen]').hidden = true;
    root.querySelector('[data-pickup-browse]').hidden = false;
    // _paintOrLoad, not render: on a deferred cart the index may never have been fetched,
    // and render() would honour the defer attribute and leave the buyer an empty list.
    HillmalayaPickup._loadingHint(root, true);
    HillmalayaPickup._paintOrLoad().then(() => HillmalayaPickup._loadingHint(root, false));
  }
});
// _pickupWrite is a side-channel for the selftest only, so it can await a write it
// otherwise has no handle to (the events themselves carry no completion signal).
document.addEventListener('pickup:selected', e => {
  HillmalayaPickup._pickupWrite = new Promise(res => window.PXUTheme.jsAjaxCart.setPickup(e.detail, res));
});
document.addEventListener('pickup:cleared', () => {
  HillmalayaPickup._pickupWrite = new Promise(res => window.PXUTheme.jsAjaxCart.clearPickup(res));
});
document.addEventListener('DOMContentLoaded', () => {
  // The seed's buttons are emitted with no text: their label carries a count and a district
  // name, and build.py has no business holding translated copy. Composed here from the same
  // __TOKEN__ template style as pickup_truncated.
  const tmpl = (window.PXUTheme.translation || {}).pickup_seed_all ||
    'Show all __COUNT__ in __DISTRICT__';
  const en = !/^zh/i.test(window.Shopify.locale || '');
  Array.prototype.forEach.call(document.querySelectorAll('[data-pickup-seed-all]'), b => {
    const box = b.closest('[data-pickup-seed-district]');
    if (!box) return;
    b.textContent = tmpl
      .replace('__COUNT__', box.getAttribute('data-pickup-seed-count'))
      .replace('__DISTRICT__', (en && box.getAttribute('data-pickup-seed-name')) ||
                               box.getAttribute('data-pickup-seed-district'));
  });
  HillmalayaPickup._wireSeedRows();
  HillmalayaPickup.render();
  // Repair a cart whose chosen point lost its delivery address at checkout. Gated on the
  // picker being on the page, which is the cart page — the one place both of
  // hm-checkout-validation's messages send the buyer.
  //
  // ⚠️ CEILING, STATED RATHER THAN HIDDEN: the MINI-CART is not covered. It renders
  // pickup-summary.liquid and this script is not loaded there, so a buyer who returns from
  // checkout and presses Checkout in the mini-cart without visiting /cart is still blocked.
  // Covering it means loading this file on every page, which is a performance decision and
  // not one to take while fixing something else. The mini-cart's own control is a link to
  // /cart, so the documented route does work.
  if (document.querySelector('[data-pickup-picker]:not([data-pickup-mode="page"])')) {
    HillmalayaPickup.reassertAddress().then(r => {
      if (r && r.action === 'reasserted') {
        console.info('pickup: delivery address re-asserted for', r.action);
      }
    });
  }
});
